'use client';

import React, { useState, useCallback } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
    SortingState,
    ColumnDef,
} from '@tanstack/react-table';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ProviderAnalysis } from '../types/dashboard';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaginatedProvidersResponse {
    providers: ProviderAnalysis[];           // ✅ backend returns "providers" not "data"
    pagination: {
        page: number;
        pageSize: number;
        total_count: number;                 // ✅ backend returns "total_count"
        total_pages: number;                 // ✅ backend returns "total_pages"
    };
}

interface FetchParams {
    page: number;
    pageSize: number;
    sort: string;
    order: 'asc' | 'desc';
    search: string;
    startDate?: string;
    endDate?: string;
}

// ─── API Fetcher ───────────────────────────────────────────────────────────────

const fetchProviders = async (params: FetchParams): Promise<PaginatedProvidersResponse> => {
    const queryParams = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
        sort: params.sort,
        order: params.order,
        ...(params.search && { search: params.search }),
        ...(params.startDate && { start_date: params.startDate }),
        ...(params.endDate && { end_date: params.endDate }),
    });

    return apiService.getProviders(queryParams.toString());
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const LoadingState: React.FC = () => (
    <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
            <div className="relative inline-block">
                <div className="w-20 h-20 border-4 border-gray-100 rounded-full absolute inset-0" />
                <div className="w-20 h-20 border-4 border-transparent border-t-blue-600 border-r-indigo-600 rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                </div>
            </div>
            <p className="mt-6 text-gray-700 font-semibold">Loading Provider Data</p>
            <p className="mt-2 text-sm text-gray-500">Processing healthcare data...</p>
        </div>
    </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-8 shadow-lg">
        <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </div>
            <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Error Loading Data</h3>
                <p className="text-red-800 mb-4">{message}</p>
                <button onClick={onRetry} className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105">
                    Try Again
                </button>
            </div>
        </div>
    </div>
);

const EmptyState: React.FC = () => (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Provider Data Available</h3>
        <p className="text-gray-600 mb-1">Upload claims data to see detailed provider table</p>
        <p className="text-sm text-gray-500">Data will appear once claims are processed</p>
    </div>
);

const SortIcon: React.FC<{ direction: 'asc' | 'desc' | false }> = ({ direction }) => (
    <span className="ml-1.5 inline-flex flex-col gap-0.5">
        <svg className={`w-3 h-3 transition-colors ${direction === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 5l7 7H5l7-7z" />
        </svg>
        <svg className={`w-3 h-3 transition-colors ${direction === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 19l-7-7h14l-7 7z" />
        </svg>
    </span>
);

// ─── Column Definition ─────────────────────────────────────────────────────────

const columnHelper = createColumnHelper<ProviderAnalysis>();

const getColumns = (isProvider: boolean): ColumnDef<ProviderAnalysis, any>[] => [
    columnHelper.display({
        id: 'rank',
        header: 'Rank',
        cell: ({ row }) => {
            const i = row.index;
            return (
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-mono font-bold shadow-xs ${
                    i === 0 ? 'bg-amber-100 text-amber-900 border border-amber-300 ring-1 ring-amber-400/40' :
                    i === 1 ? 'bg-slate-200 text-slate-800 border border-slate-300' :
                    i === 2 ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                    'bg-muted text-muted-foreground'
                }`}>
                    #{i + 1}
                </span>
            );
        },
        enableSorting: false,
    }),
    columnHelper.accessor('name', {
        header: isProvider ? 'Payer Name' : 'Provider Code',
        cell: (info) => (
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                </div>
                <span className="text-sm font-bold text-gray-900">{info.getValue()}</span>
            </div>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor('claims', {
        header: 'Claims',
        cell: (info) => (
            <span className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-bold rounded-lg">
                {formatNumber(info.getValue())}
            </span>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor('totalAmount', {
        header: 'Total Amount',
        cell: (info) => (
            <span className="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-700 text-sm font-bold rounded-lg">
                {formatCurrency(info.getValue())}
            </span>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor('avgAmount', {
        header: 'Avg per Claim',
        cell: (info) => (
            <span className="inline-flex items-center px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-bold rounded-lg">
                {formatCurrency(info.getValue())}
            </span>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor('percentage', {
        header: 'Market Share',
        cell: (info) => {
            const pct = info.getValue() as number;
            return (
                <div className="flex items-center justify-end gap-2">
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full ${
                                pct > 10 ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                                pct > 5  ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                                           'bg-gradient-to-r from-green-500 to-emerald-500'
                            }`}
                            style={{ width: `${Math.min(pct * 5, 100)}%` }}
                        />
                    </div>
                    <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-lg ${
                        pct > 10 ? 'bg-red-100 text-red-700' :
                        pct > 5  ? 'bg-yellow-100 text-yellow-700' :
                                   'bg-green-100 text-green-700'
                    }`}>
                        {pct.toFixed(1)}%
                    </span>
                </div>
            );
        },
        enableSorting: true,
    }),
];

// ─── Main Component ────────────────────────────────────────────────────────────

const ProviderTableView: React.FC = () => {
    const { user } = useAuth();
    const isProvider = user?.company_type === 'provider';
    const entityLabel = isProvider ? 'Payer' : 'Provider';

    const [page, setPage]               = useState(1);
    const [pageSize, setPageSize]       = useState(20);
    const [search, setSearch]           = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [startDate, setStartDate]     = useState('');
    const [endDate, setEndDate]         = useState('');

    const [sorting, setSorting] = useState<SortingState>([
        { id: 'claims', desc: true },
    ]);

    const sortField = sorting[0]?.id ?? 'claims';
    const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

    // ── TanStack Query ────────────────────────────────────────────────────────
    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['providers', page, pageSize, sortField, sortOrder, search, startDate, endDate],
        queryFn: () => fetchProviders({ page, pageSize, sort: sortField, order: sortOrder, search, startDate, endDate }),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });

    // ✅ FIXED: "providers" not "data" — matches backend response
    const rows       = data?.providers ?? [];
    const pagination = data?.pagination;
    // ✅ FIXED: "total_pages" not "totalPages" — matches backend snake_case
    const totalPages = pagination?.total_pages ?? 1;

    const tableColumns = React.useMemo(() => getColumns(isProvider), [isProvider]);

    // ── TanStack Table ────────────────────────────────────────────────────────
    const table = useReactTable({
        data: rows,
        columns: tableColumns,
        state: { sorting },
        onSortingChange: (updater) => {
            setSorting(updater);
            setPage(1);
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualSorting: true,
        manualPagination: true,
        pageCount: totalPages,
    });

    // ── Search debounce 400ms ─────────────────────────────────────────────────
    const searchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSearchChange = useCallback((val: string) => {
        setSearchInput(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setSearch(val);
            setPage(1);
        }, 400);
    }, []);

    const totalClaims = rows.reduce((s, p) => s + p.claims, 0);
    const totalAmount = rows.reduce((s, p) => s + p.totalAmount, 0);
    const avgClaimVal = totalClaims > 0 ? totalAmount / totalClaims : 0;

    const handleClear = () => {
        setSearch(''); setSearchInput('');
        setStartDate(''); setEndDate('');
        setPageSize(20); setPage(1);
        setSorting([{ id: 'claims', desc: true }]);
    };

    const hasFilters = search || startDate || endDate || pageSize !== 20;

    return (
        <div className="space-y-6">

            {/* ── Filters bar ── */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-gray-700">
                        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                        <span className="text-sm font-semibold">Filters:</span>
                    </div>

                    <div className="relative min-w-[200px]">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder={`Search ${entityLabel.toLowerCase()}...`}
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm text-gray-700 transition-all hover:border-blue-400"
                        />
                    </div>

                    <div className="relative min-w-[160px]">
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm font-medium text-gray-700 transition-all hover:border-blue-400 appearance-none"
                        >
                            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
                        </select>
                        <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>

                    <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-medium text-gray-700 hover:border-blue-400 min-w-[150px]" />
                    <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-medium text-gray-700 hover:border-blue-400 min-w-[150px]" />

                    {hasFilters && (
                        <button onClick={handleClear} className="ml-auto px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-200 flex items-center gap-1.5">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <LoadingState />
            ) : isError ? (
                <ErrorState message={(error as Error).message} onRetry={refetch} />
            ) : rows.length === 0 ? (
                <EmptyState />
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { label: 'Total Claims',  value: formatNumber(totalClaims),   color: 'blue',   badge: 'Total',
                              icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
                            { label: 'Total Value',   value: formatCurrency(totalAmount), color: 'green',  badge: 'Amount',
                              icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
                            { label: 'Avg per Claim', value: formatCurrency(avgClaimVal), color: 'purple', badge: 'Average',
                              icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                        ].map(({ label, value, color, badge, icon }) => (
                            <div key={label} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow duration-200">
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`w-12 h-12 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-xl flex items-center justify-center shadow-lg`}>
                                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                                        </svg>
                                    </div>
                                    <span className={`px-3 py-1 bg-${color}-50 text-${color}-700 text-xs font-bold rounded-full`}>{badge}</span>
                                </div>
                                <p className="text-sm text-gray-600 font-medium mb-1">{label}</p>
                                <p className="text-3xl font-bold text-gray-900">{value}</p>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-5 border-b border-gray-200">
                            <h4 className="text-xl font-bold text-gray-900">{entityLabel} Performance Details</h4>
                            <p className="text-sm text-gray-600 mt-1">
                                {/* ✅ FIXED: total_count matches backend */}
                                {pagination ? `${pagination.total_count.toLocaleString()} ${entityLabel.toLowerCase()}s total` : `Comprehensive breakdown of ${entityLabel.toLowerCase()} metrics`}
                                {isFetching && !isLoading && <span className="ml-2 text-blue-500 text-xs animate-pulse">Refreshing…</span>}
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                                    {table.getHeaderGroups().map(hg => (
                                        <tr key={hg.id}>
                                            {hg.headers.map(header => {
                                                const canSort = header.column.getCanSort();
                                                const sorted  = header.column.getIsSorted();
                                                return (
                                                    <th key={header.id}
                                                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                                        className={`px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider select-none
                                                            ${header.id === 'rank' || header.id === 'name' ? 'text-left' : 'text-right'}
                                                            ${canSort ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}
                                                        `}
                                                    >
                                                        <span className="inline-flex items-center gap-1">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {canSort && <SortIcon direction={sorted} />}
                                                        </span>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {table.getRowModel().rows.map(row => (
                                        <tr key={row.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-150">
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id}
                                                    className={`px-6 py-5 whitespace-nowrap ${
                                                        cell.column.id === 'rank' || cell.column.id === 'name' ? '' : 'text-right'
                                                    }`}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                                <span className="text-gray-600">
                                    Page <span className="font-bold text-gray-900">{page}</span> of{' '}
                                    <span className="font-bold text-gray-900">{totalPages}</span>
                                    {pagination && (
                                        // ✅ FIXED: total_count
                                        <> · <span className="font-bold text-gray-900">{pagination.total_count.toLocaleString()}</span> total</>
                                    )}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setPage(1)} disabled={page === 1 || isFetching}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs font-semibold">
                                        « First
                                    </button>
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || isFetching}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs font-semibold">
                                        ‹ Prev
                                    </button>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                                        const p = start + i;
                                        return (
                                            <button key={p} onClick={() => setPage(p)} disabled={isFetching}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                    p === page ? 'bg-blue-600 text-white shadow-md'
                                                               : 'border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40'
                                                }`}>
                                                {p}
                                            </button>
                                        );
                                    })}
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || isFetching}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs font-semibold">
                                        Next ›
                                    </button>
                                    <button onClick={() => setPage(totalPages)} disabled={page === totalPages || isFetching}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs font-semibold">
                                        Last »
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProviderTableView;